import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

import {
  ApiError,
  api,
  leaveKindLabels,
  monthKey,
  type Client,
  type EmployeesView,
  type ImportChange,
  type ImportRun,
  type TimeEntry,
  type TimesheetDeveloper,
  type TimesheetView,
} from '../api.js';
import { replaceUrl } from '../router.js';
import { pageStyles } from '../shared-styles.js';
import '../components/leave-dialog.js';
import '../components/holidays-dialog.js';
import type { LeaveDialog } from '../components/leave-dialog.js';
import type { HolidaysDialog } from '../components/holidays-dialog.js';

/** Everhour hours per client and month, with import and change review. */
@customElement('timesheets-page')
export class TimesheetsPage extends LitElement {
  static styles = pageStyles;

  @state() private mode: 'clients' | 'employees' = 'clients';
  @state() private employeesView: EmployeesView | null = null;
  @state() private clients: Client[] = [];
  @state() private clientId = '';
  @state() private month = monthKey(new Date());
  @state() private view: TimesheetView | null = null;
  @state() private loading = false;
  @state() private error = '';
  @state() private importing = false;
  @state() private importResult: ImportRun | null = null;
  @state() private expanded: Record<number, TimeEntry[] | 'loading' | undefined> = {};
  // Changes dialog
  @state() private runsOpen = false;
  @state() private runs: ImportRun[] = [];
  @state() private selectedRun: ImportRun | null = null;
  @state() private changes: ImportChange[] = [];

  connectedCallback() {
    super.connectedCallback();
    const p = new URLSearchParams(window.location.search);
    this.clientId = p.get('client_id') ?? '';
    this.month = p.get('month') ?? this.month;
    this.mode = p.get('view') === 'employees' ? 'employees' : 'clients';
    void this.init();
  }

  private async init() {
    try {
      this.clients = await api.get<Client[]>('/api/clients');
      if (!this.clientId && this.clients.length === 1) this.clientId = String(this.clients[0].id);
    } catch (e) {
      this.error = (e as Error).message;
    }
    await this.load();
  }

  private async load() {
    this.expanded = {};
    this.importResult = null;
    const params = new URLSearchParams({ month: this.month });
    if (this.mode === 'employees') params.set('view', 'employees');
    if (this.clientId) params.set('client_id', this.clientId);
    replaceUrl(`/timesheets?${params}`);
    if (this.mode === 'employees') {
      this.loading = true;
      this.error = '';
      try {
        this.employeesView = await api.get<EmployeesView>(`/api/timesheets/employees?month=${this.month}`);
      } catch (e) {
        this.error = (e as Error).message;
        this.employeesView = null;
      } finally {
        this.loading = false;
      }
      return;
    }
    if (!this.clientId) {
      this.view = null;
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      this.view = await api.get<TimesheetView>(`/api/timesheets?client_id=${this.clientId}&month=${this.month}`);
    } catch (e) {
      this.error = (e as Error).message;
      this.view = null;
    } finally {
      this.loading = false;
    }
  }

  private async runImport() {
    if (!this.clientId) return;
    this.importing = true;
    this.error = '';
    try {
      this.importResult = await api.post<ImportRun>('/api/timesheets/import', { client_id: Number(this.clientId), month: this.month });
      const result = this.importResult;
      await this.load();
      this.importResult = result;
    } catch (e) {
      if (e instanceof ApiError) {
        const hint = Object.values(e.fields)[0];
        this.error = hint ? `${e.message}: ${hint}` : e.message;
      } else {
        this.error = (e as Error).message;
      }
    } finally {
      this.importing = false;
    }
  }

  private async toggle(d: TimesheetDeveloper) {
    if (this.expanded[d.contract_id] !== undefined) {
      const next = { ...this.expanded };
      delete next[d.contract_id];
      this.expanded = next;
      return;
    }
    this.expanded = { ...this.expanded, [d.contract_id]: 'loading' };
    try {
      const entries = await api.get<TimeEntry[]>(`/api/timesheets/entries?contract_id=${d.contract_id}&month=${this.month}`);
      this.expanded = { ...this.expanded, [d.contract_id]: entries };
    } catch (e) {
      this.error = (e as Error).message;
      const next = { ...this.expanded };
      delete next[d.contract_id];
      this.expanded = next;
    }
  }

  private async openRuns() {
    this.runsOpen = true;
    this.runs = [];
    this.selectedRun = null;
    this.changes = [];
    try {
      this.runs = await api.get<ImportRun[]>(`/api/timesheets/runs?client_id=${this.clientId}&month=${this.month}`);
      const first = this.runs.find((r) => r.changes > 0) ?? this.runs[0];
      if (first) await this.selectRun(first);
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  private async selectRun(run: ImportRun) {
    this.selectedRun = run;
    this.changes = [];
    try {
      this.changes = await api.get<ImportChange[]>(`/api/timesheets/runs/${run.id}/changes`);
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  private onDialogHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.runsOpen = false;
  };

  // ---- render ----

  render() {
    return html`
      <div class="page-header">
        <h2>Timesheets</h2>
        <wa-button size="small" appearance=${this.mode === 'clients' ? 'filled' : 'outlined'} @click=${() => this.setMode('clients')}>By client</wa-button>
        <wa-button size="small" appearance=${this.mode === 'employees' ? 'filled' : 'outlined'} @click=${() => this.setMode('employees')}>Employees</wa-button>
        <span class="spacer"></span>
        ${this.mode === 'clients'
          ? html`
              ${this.view?.last_run
                ? html`<span class="muted">Last import ${formatWhen(this.view.last_run.finished_at ?? this.view.last_run.started_at)} by ${this.view.last_run.actor}</span>`
                : nothing}
              <wa-button appearance="outlined" ?disabled=${!this.clientId} @click=${this.openRuns}>Changes</wa-button>
              <wa-button variant="brand" ?disabled=${!this.clientId} ?loading=${this.importing} @click=${this.runImport}>Import from Everhour</wa-button>
            `
          : html`<wa-button appearance="outlined" @click=${this.openHolidays}>Holidays ${this.month.slice(0, 4)}</wa-button>`}
      </div>
      <div class="toolbar">
        ${this.mode === 'clients'
          ? html`
              <wa-select label="Client" .value=${this.clientId} @change=${(e: Event) => { this.clientId = (e.target as HTMLSelectElement).value; void this.load(); }}>
                ${this.clients.map((c) => html`<wa-option value=${String(c.id)}>${c.name}${c.everhour_token_set ? '' : ' (no Everhour token)'}</wa-option>`)}
              </wa-select>
            `
          : nothing}
        <wa-input label="Month" type="month" .value=${this.month} @change=${(e: Event) => { const v = (e.target as HTMLInputElement).value; if (v) { this.month = v; void this.load(); } }}></wa-input>
      </div>
      ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
      ${this.mode === 'clients' ? html`${this.renderImportResult()} ${this.renderTable()}` : this.renderEmployees()}
      ${this.renderRunsDialog()}
      <leave-dialog @closed=${() => this.load()}></leave-dialog>
      <holidays-dialog @closed=${() => this.load()}></holidays-dialog>
    `;
  }

  private setMode(mode: 'clients' | 'employees') {
    if (this.mode === mode) return;
    this.mode = mode;
    void this.load();
  }

  private openLeave(employeeId: number, name: string) {
    const v = this.employeesView;
    if (!v) return;
    void (this.renderRoot.querySelector('leave-dialog') as LeaveDialog).show(employeeId, name, v.from, v.to);
  }

  private openHolidays() {
    void (this.renderRoot.querySelector('holidays-dialog') as HolidaysDialog).show(Number(this.month.slice(0, 4)));
  }

  private renderEmployees() {
    if (this.loading) return html`<wa-spinner></wa-spinner>`;
    const v = this.employeesView;
    if (!v) return nothing;
    if (v.employees.length === 0) return html`<div class="empty">No employees.</div>`;
    return html`
      ${v.holidays.length
        ? html`<p class="muted">Holidays in ${v.month}: ${v.holidays.map((h) => `${h.date.slice(8)} ${h.name}`).join(' · ')}</p>`
        : html`<p class="muted">No holidays recorded in ${v.month}.</p>`}
      ${v.is_previous_month ? nothing : html`<p class="muted">Shortfalls are alerts only for the previous month; this month is shown for information.</p>`}
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th>Employee</th>
            <th class="num">Logged</th>
            <th>By client</th>
            <th class="num">Vacation</th>
            <th class="num">Sick</th>
            <th class="num">Other</th>
            <th class="num">Working days</th>
            <th class="num">Expected</th>
            <th class="num">Difference</th>
            <th class="num">Vacation balance</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${v.employees.map((e) => {
            const short = e.difference !== null && Number(e.difference) < 0;
            const alert = short && v.is_previous_month;
            return html`
              <tr>
                <td data-label="Employee">
                  ${e.name}
                  <span class="sub">${e.hours_per_day} h/day</span>
                </td>
                <td class="num" data-label="Logged"><strong>${e.hours_total}</strong></td>
                <td class="m-hide" data-label="Clients">${e.hours_by_client.map((c) => html`<span class="sub">${c.client_name}: ${c.hours}</span>`)}</td>
                <td class="num" data-label="Vacation">${e.leave.vacation ?? html`<span class="muted">—</span>`}</td>
                <td class="num m-hide" data-label="Sick">${e.leave.sick ?? html`<span class="muted">—</span>`}</td>
                <td class="num m-hide" data-label="Other">${e.leave.other ?? html`<span class="muted">—</span>`}</td>
                <td class="num m-hide" data-label="Work days">${e.working_days}${e.holidays ? html`<span class="sub">${e.holidays} holiday${e.holidays > 1 ? 's' : ''}</span>` : nothing}</td>
                <td class="num" data-label="Expected">${e.expected_hours ?? html`<span class="muted">—</span>`}</td>
                <td class="num ${alert ? 'warn' : ''}" data-label="Diff.">${e.difference === null ? html`<span class="muted">—</span>` : Number(e.difference) > 0 ? `+${e.difference}` : e.difference}</td>
                <td class="num" data-label="Vac. balance">
                  ${e.vacation_balance
                    ? html`${e.vacation_balance.balance}<span class="sub">${e.vacation_balance.remaining} as of ${e.vacation_balance.as_of}, ${e.vacation_balance.taken_since} taken</span>`
                    : html`<span class="muted">no anchor</span>`}
                </td>
                <td class="actions">
                  <wa-button size="small" appearance="plain" @click=${() => this.openLeave(e.employee_id, e.name)}>Leave</wa-button>
                </td>
              </tr>
            `;
          })}
        </tbody>
      </table></div>
    `;
  }

  private renderImportResult() {
    const r = this.importResult;
    if (!r || !r.summary) return nothing;
    const s = r.summary;
    const problems = s.unmatched_users.length + s.contracts_without_everhour_id.length + (s.unresolved > 0 ? 1 : 0);
    return html`
      <wa-callout variant=${r.status === 'failed' ? 'danger' : problems ? 'warning' : 'success'}>
        <div class="check-lines">
          <div>
            Imported <strong>${s.entries}</strong> entries (${s.hours} h): ${s.added} added, ${s.changed} changed, ${s.removed} removed ·
            ${s.leave_days} leave days${s.leave_removed ? `, ${s.leave_removed} removed` : ''}${s.holidays_added ? `, ${s.holidays_added} new holidays` : ''}
          </div>
          ${s.unresolved ? html`<div class="warn">${s.unresolved} entries could not be routed to a billing group (check the rules and the contracts' default groups)</div>` : nothing}
          ${s.invoiced_changes ? html`<div class="warn">${s.invoiced_changes} changed or removed entries are already on an issued invoice; check the Invoices tab</div>` : nothing}
          ${s.unmatched_users.map((u) => html`<div class="warn">Everhour user ${u.id} (${u.name}) logged ${u.hours} h in ${u.entries} entries without a contract covering those dates</div>`)}
          ${s.contracts_without_everhour_id.length ? html`<div class="warn">Contracts without an Everhour user ID: ${s.contracts_without_everhour_id.join(', ')}</div>` : nothing}
        </div>
      </wa-callout>
    `;
  }

  private renderTable() {
    if (!this.clientId) return html`<div class="empty">Choose a client.</div>`;
    if (this.loading) return html`<wa-spinner></wa-spinner>`;
    const v = this.view;
    if (!v) return nothing;
    if (v.developers.length === 0) return html`<div class="empty">No contracts of this client overlap ${v.month}.</div>`;
    const anyUnresolved = v.developers.some((d) => d.unresolved_hours !== '0.00');
    return html`
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th>Developer</th>
            ${v.groups.map((g) => html`<th class="num wrap">${g.name}</th>`)}
            ${anyUnresolved ? html`<th class="num warn">Unresolved</th>` : nothing}
            <th class="num">Total</th>
            <th class="num">Leave</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${v.developers.map((d) => this.renderDeveloper(d, v, anyUnresolved))}
          <tr>
            <th data-label="Total">All developers</th>
            ${v.groups.map((g) => html`<th class="num billing-group" data-label=${g.name}>${sum(v.developers.map((d) => d.hours_by_group[String(g.id)]))}</th>`)}
            ${anyUnresolved ? html`<th class="num warn" data-label="Unresolved">${sum(v.developers.map((d) => d.unresolved_hours))}</th>` : nothing}
            <th class="num" data-label="Total">${sum(v.developers.map((d) => d.hours_total))}</th>
            <th class="num" data-label="Leave">${sum(v.developers.map((d) => sum(d.leave.map((l) => l.fraction))))}</th>
            <th class="actions"></th>
          </tr>
        </tbody>
      </table></div>
    `;
  }

  private renderDeveloper(d: TimesheetDeveloper, v: TimesheetView, anyUnresolved: boolean) {
    const open = this.expanded[d.contract_id];
    const leaveDays = sum(d.leave.map((l) => l.fraction));
    return html`
      <tr class=${open !== undefined ? 'expanded' : ''}>
        <td data-label="Developer">
          ${d.name}
          ${d.everhour_user_id ? nothing : html`<span class="sub warn">no Everhour user ID on the contract</span>`}
        </td>
        ${v.groups.map((g) => html`<td class="num" data-label=${g.name}>${d.hours_by_group[String(g.id)] ?? html`<span class="muted">—</span>`}</td>`)}
        ${anyUnresolved ? html`<td class="num ${d.unresolved_hours !== '0.00' ? 'warn' : ''}" data-label="Unresolved">${d.unresolved_hours !== '0.00' ? d.unresolved_hours : html`<span class="muted">—</span>`}</td>` : nothing}
        <td class="num" data-label="Total"><strong>${d.hours_total}</strong></td>
        <td class="num" data-label="Leave">${leaveDays !== '0.00' ? leaveDays : html`<span class="muted">—</span>`}</td>
        <td class="actions">
          <wa-button size="small" appearance="plain" @click=${() => this.toggle(d)}>${open !== undefined ? 'Hide' : `Days & entries (${d.entries})`}</wa-button>
        </td>
      </tr>
      ${open !== undefined
        ? html`
            <tr class="expanded">
              <td colspan=${v.groups.length + 4 + (anyUnresolved ? 1 : 0)}>
                ${this.renderDays(d, v)}
                ${open === 'loading' ? html`<wa-spinner></wa-spinner>` : this.renderEntries(open)}
              </td>
            </tr>
          `
        : nothing}
    `;
  }

  private renderDays(d: TimesheetDeveloper, v: TimesheetView) {
    const leaveByDate = new Map(d.leave.map((l) => [l.date, l]));
    const days: string[] = [];
    for (let t = Date.parse(v.from); t <= Date.parse(v.to); t += 86400000) days.push(new Date(t).toISOString().slice(0, 10));
    return html`
      <div class="day-grid">
        ${days.map((date) => {
          const wd = new Date(date + 'T00:00:00Z').getUTCDay();
          const leave = leaveByDate.get(date);
          const cls = ['day', wd === 0 || wd === 6 ? 'weekend' : '', leave?.kind ?? ''].join(' ');
          return html`
            <div class=${cls} title=${leave ? `${leaveKindLabels[leave.kind]}${leave.note ? ` · ${leave.note}` : ''}` : date}>
              <div class="d">${Number(date.slice(8))}</div>
              <div>${d.days[date] ?? (leave ? leaveKindLabels[leave.kind].slice(0, 1) + (leave.fraction !== '1.00' ? ` ${leave.fraction}` : '') : '')}</div>
            </div>
          `;
        })}
      </div>
    `;
  }

  private renderEntries(entries: TimeEntry[]) {
    if (entries.length === 0) return html`<div class="muted">No entries.</div>`;
    return html`
      <div class="table-wrap"><table class="entries">
        <thead>
          <tr>
            <th>Date</th>
            <th class="num">Hours</th>
            <th>Task</th>
            <th>Group</th>
            <th>Attributes</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
          ${entries.map(
            (e) => html`
              <tr>
                <td>
                  ${e.date}${e.is_locked ? html` <wa-tag size="small">locked</wa-tag>` : nothing}
                  ${e.invoice_id ? html` <wa-tag size="small" variant="success" title="On an issued invoice">${e.invoice_number ?? 'invoiced'}</wa-tag>` : nothing}
                </td>
                <td class="num">${e.hours}</td>
                <td>
                  ${e.task_url ? html`<a href=${e.task_url} target="_blank" rel="noopener">${e.task_name ?? e.task_id}</a>` : (e.task_name ?? e.task_id ?? html`<span class="muted">—</span>`)}
                  ${e.task_number ? html`<span class="sub">${e.task_number}</span>` : nothing}
                </td>
                <td>${e.billing_group ? html`${e.billing_group} <span class="sub">${e.resolved_by}</span>` : html`<span class="warn">unresolved</span>`}</td>
                <td>${Object.entries(e.attributes).map(([k, val]) => html`<span class="sub">${k}: ${val}</span>`)}</td>
                <td>${e.comment ?? html`<span class="muted">—</span>`}</td>
              </tr>
            `,
          )}
        </tbody>
      </table></div>
    `;
  }

  private renderRunsDialog() {
    return html`
      <wa-dialog label="Import runs and changes" ?open=${this.runsOpen} @wa-after-hide=${this.onDialogHidden} style="--width: min(60rem, calc(100vw - 1.5rem));">
        ${this.runs.length === 0 ? html`<div class="empty">No imports for this month yet.</div>` : nothing}
        <div class="chips">
          ${this.runs.map(
            (r) => html`
              <wa-button size="small" appearance=${this.selectedRun?.id === r.id ? 'filled' : 'outlined'} variant=${r.status === 'failed' ? 'danger' : 'neutral'} @click=${() => this.selectRun(r)}>
                ${formatWhen(r.started_at)} · ${r.actor} · ${r.status}${r.status === 'done' ? ` · ${r.changes} changes` : ''}
              </wa-button>
            `,
          )}
        </div>
        ${this.selectedRun?.error ? html`<wa-callout variant="danger">${this.selectedRun.error}</wa-callout>` : nothing}
        ${this.selectedRun && this.changes.length === 0 && this.selectedRun.status === 'done' ? html`<p class="muted">This run changed nothing.</p>` : nothing}
        ${this.changes.length
          ? html`
              <div class="table-wrap"><table class="entries">
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th>Developer</th>
                    <th>Date</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  ${this.changes.map(
                    (c) => html`
                      <tr>
                        <td><wa-tag size="small" variant=${c.kind === 'added' ? 'success' : c.kind === 'removed' ? 'danger' : 'brand'}>${c.kind}</wa-tag></td>
                        <td>${c.employee_name}</td>
                        <td>${c.date}</td>
                        <td>${describeChange(c)}</td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table></div>
            `
          : nothing}
      </wa-dialog>
    `;
  }
}

function describeChange(c: ImportChange): string {
  if (c.kind === 'added' && c.after) return `${c.after.hours} h · ${c.after.task_name ?? ''} → ${c.after.billing_group ?? 'unresolved'}`;
  if (c.kind === 'removed' && c.before) return `${c.before.hours} h · ${c.before.task_name ?? ''} (no longer in Everhour)`;
  if (c.before && c.after) {
    const diffs: string[] = [];
    if (c.before.hours !== c.after.hours) diffs.push(`${c.before.hours} h → ${c.after.hours} h`);
    if (c.before.date !== c.after.date) diffs.push(`date ${c.before.date} → ${c.after.date}`);
    if (c.before.task_name !== c.after.task_name) diffs.push(`task ${c.before.task_name ?? '—'} → ${c.after.task_name ?? '—'}`);
    if (c.before.billing_group !== c.after.billing_group) diffs.push(`group ${c.before.billing_group ?? 'unresolved'} → ${c.after.billing_group ?? 'unresolved'}`);
    if (c.before.comment !== c.after.comment) diffs.push('comment changed');
    if (c.before.is_locked !== c.after.is_locked) diffs.push(c.after.is_locked ? 'locked' : 'unlocked');
    return diffs.join(', ') || 'details changed';
  }
  return '';
}

/** Sums decimal strings like "7.50" into one, as a string. */
function sum(values: (string | undefined)[]): string {
  const cents = values.reduce((acc, v) => acc + Math.round(Number(v ?? 0) * 100), 0);
  return (cents / 100).toFixed(2);
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

declare global {
  interface HTMLElementTagNameMap {
    'timesheets-page': TimesheetsPage;
  }
}
